import { Component } from '@theme/component';
import { ThemeEvents, VariantUpdateEvent, ZoomMediaSelectedEvent } from '@theme/events';

/**
 * A custom element that renders a media gallery.
 *
 * @typedef {object} Refs
 * @property {import('./zoom-dialog').ZoomDialog} [zoomDialogComponent] - The zoom dialog component.
 * @property {import('./slideshow').Slideshow} [slideshow] - The slideshow component.
 * @property {HTMLElement[]} [media] - The media elements.
 *
 * @extends Component<Refs>
 */
export class MediaGallery extends Component {
  #touchGesture = null;
  #touchSwipeScroller = null;
  #touchSetupFrame = 0;

  connectedCallback() {
    super.connectedCallback();

    const { signal } = this.#controller;
    const target = this.closest('.shopify-section, dialog');

    target?.addEventListener(ThemeEvents.variantUpdate, this.#handleVariantUpdate, { signal });
    this.refs.zoomDialogComponent?.addEventListener(ThemeEvents.zoomMediaSelected, this.#handleZoomMediaSelected, {
      signal,
    });

    this.#queueTouchSwipeSetup(signal);
  }

  #controller = new AbortController();

  disconnectedCallback() {
    super.disconnectedCallback();

    if (this.#touchSetupFrame) {
      cancelAnimationFrame(this.#touchSetupFrame);
      this.#touchSetupFrame = 0;
    }

    this.#controller.abort();
  }

  /**
   * Handles a variant update event by replacing the current media gallery with a new one.
   *
   * @param {VariantUpdateEvent} event - The variant update event.
   */
  #handleVariantUpdate = (event) => {
    const source = event.detail.data.html;

    if (!source) return;
    const newMediaGallery = source.querySelector('media-gallery');

    if (!newMediaGallery) return;

    this.replaceWith(newMediaGallery);
  };

  /**
   * Handles the 'zoom-media:selected' event.
   * @param {ZoomMediaSelectedEvent} event - The zoom-media:selected event.
   */
  #handleZoomMediaSelected = async (event) => {
    this.slideshow?.select(event.detail.index, undefined, { animate: false });
  };

  /**
   * Zooms the media gallery.
   *
   * @param {number} index - The index of the media to zoom.
   * @param {PointerEvent} event - The pointer event.
   */
  zoom(index, event) {
    this.refs.zoomDialogComponent?.open(index, event);
  }

  /**
   * Preloads an image.
   * @param {number} index - The index of the media to preload.
   */
  preloadImage(index) {
    const zoomDialogMedia = this.refs.zoomDialogComponent?.refs.media[index];
    if (!zoomDialogMedia) return;

    this.refs.zoomDialogComponent?.loadHighResolutionImage(zoomDialogMedia);
  }

  get slideshow() {
    return this.refs.slideshow;
  }

  get media() {
    return this.refs.media;
  }

  get presentation() {
    return this.dataset.presentation;
  }

  #queueTouchSwipeSetup(signal, attempt = 0) {
    if (signal.aborted) return;

    if (this.#setupTouchSwipe(signal)) return;
    if (attempt >= 20) return;

    this.#touchSetupFrame = requestAnimationFrame(() => {
      this.#touchSetupFrame = 0;
      this.#queueTouchSwipeSetup(signal, attempt + 1);
    });
  }

  #setupTouchSwipe(signal) {
    const scroller = this.slideshow?.refs?.scroller;
    if (!(scroller instanceof HTMLElement)) return false;
    if (this.#touchSwipeScroller === scroller) return true;

    scroller.addEventListener('touchstart', this.#handleTouchStart, { signal, passive: true });
    scroller.addEventListener('touchmove', this.#handleTouchMove, { signal, passive: false });
    scroller.addEventListener('touchend', this.#handleTouchEnd, { signal, passive: true });
    scroller.addEventListener('touchcancel', this.#resetTouchGesture, { signal });

    this.#touchSwipeScroller = scroller;
    return true;
  }

  #handleTouchStart = (event) => {
    if (this.presentation !== 'carousel') return;
    if (window.matchMedia('(min-width: 750px)').matches) return;
    if (event.touches.length !== 1) {
      this.#touchGesture = null;
      return;
    }

    const touch = event.touches[0];
    if (!touch) return;

    this.#touchGesture = {
      startX: touch.clientX,
      startY: touch.clientY,
      deltaX: 0,
      deltaY: 0,
    };
  };

  #handleTouchMove = (event) => {
    if (!this.#touchGesture) return;

    const touch = event.touches[0];
    if (!touch) return;

    this.#touchGesture.deltaX = touch.clientX - this.#touchGesture.startX;
    this.#touchGesture.deltaY = touch.clientY - this.#touchGesture.startY;

    if (Math.abs(this.#touchGesture.deltaX) > Math.abs(this.#touchGesture.deltaY) && event.cancelable) {
      event.preventDefault();
    }
  };

  #handleTouchEnd = () => {
    if (!this.#touchGesture) {
      this.#touchGesture = null;
      return;
    }

    const { deltaX, deltaY } = this.#touchGesture;
    const swipeThreshold = 48;
    const isHorizontalSwipe = Math.abs(deltaX) > swipeThreshold && Math.abs(deltaX) > Math.abs(deltaY);

    if (isHorizontalSwipe) {
      this.#selectSwipeTarget(deltaX < 0 ? 1 : -1);
    }

    this.#touchGesture = null;
  };

  #resetTouchGesture = () => {
    this.#touchGesture = null;
  };

  #selectSwipeTarget(direction) {
    const slideshow = this.slideshow;
    const slides = slideshow?.slides;
    if (!slideshow || !slides?.length) return;

    const currentIndex = slideshow.current;
    const lastIndex = slides.length - 1;
    let targetIndex = currentIndex + direction;

    if (slideshow.infinite) {
      if (targetIndex < 0) {
        targetIndex = lastIndex;
      } else if (targetIndex > lastIndex) {
        targetIndex = 0;
      }
    } else {
      targetIndex = Math.max(0, Math.min(lastIndex, targetIndex));
    }

    if (targetIndex === currentIndex) return;

    requestAnimationFrame(() => {
      slideshow.select(targetIndex, undefined, { animate: false });
    });
  }
}

if (!customElements.get('media-gallery')) {
  customElements.define('media-gallery', MediaGallery);
}
